import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ProjectDisclaimer from "@/components/ProjectDisclaimer";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | AILens</title>
        <meta name="description" content="Usage guidelines for AILens. Read our fair use policy for the AI visibility scanner." />
        <meta property="og:title" content="Terms of Service | AILens" />
        <meta property="og:description" content="Usage guidelines for AILens. Read our fair use policy for the AI visibility scanner." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ai-lens.app/terms" />
        <link rel="canonical" href="https://ai-lens.app/terms" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Terms of Service | AILens" />
        <meta name="twitter:description" content="Usage guidelines for AILens." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="container max-w-3xl mx-auto px-4 py-12">
          
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Scanner
          </Link>

          <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-8">Last updated: February 2026</p>

          <div className="prose prose-gray max-w-none">
            
            <h2 className="text-xl font-semibold mt-8 mb-4">Service Description</h2>
            <p className="mb-4">AILens provides AI visibility scanning services.</p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li><strong>Automated Scans:</strong> Checks ChatGPT visibility and provides a score with recommendations.</li>
              <li><strong>Technical AI Checks:</strong> Analyzes your website's public configuration for AI crawler accessibility.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Experimental Nature</h2>
            <p className="mb-4">
              AILens is an experimental research project provided as-is. The tool was built as a research project exploring AI visibility.
            </p>
            <p className="mb-2">This means:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Features and scoring methodology are fixed as of February 2026</li>
            </ul>
            <p className="mb-6">
              By using AILens, you acknowledge that the service is an experimental research project and accept that it is provided as-is.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Acceptable Use</h2>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>For legitimate brand monitoring only</li>
              <li>No automated scraping or system abuse</li>
              <li>Accurate email information required if you choose to contact us</li>
              <li>Current limits: 50 scans per hour per IP address</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Limitations and Accuracy</h2>
            
            <h3 className="text-lg font-medium mt-4 mb-3">General</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>All results are informational estimates and should not be treated as definitive measurements. AI visibility scores are calculated using our proprietary methodology and represent an approximation, not an absolute metric.</li>
              <li>AI platform responses change frequently. A scan performed today may return different results tomorrow, even for the same brand and keyword. This is inherent to how AI models work and is not a defect in our service.</li>
              <li>Recommendations provided are general best practices based on published research. They do not constitute professional SEO, marketing, or business advice. Consult qualified professionals for decisions affecting your business.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-3">Technical AI Checks</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>These checks analyze publicly available files and server-rendered HTML only. Schema markup or structured data injected via JavaScript (e.g., Google Tag Manager, client-side rendering frameworks) will not be detected.</li>
              <li>The robots.txt analysis checks common AI crawler directives (GPTBot, ClaudeBot, PerplexityBot, ChatGPT-User, Google-Extended) but may not account for every possible robots.txt configuration or edge case.</li>
              <li>Sitemap detection uses a three-step fallback (robots.txt Sitemap directive, /sitemap.xml, /sitemap_index.xml) but may not find sitemaps at non-standard locations.</li>
              <li>Results are best-effort and may contain inaccuracies. Do not rely solely on these results for technical decisions.</li>
              <li>Unblocking a specific AI crawler in your robots.txt does not guarantee that the respective AI platform will index or cite your content.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-3">Third-Party Dependencies</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Our service relies on third-party APIs for core functionality. Changes to these services, rate limits, downtime, or policy updates may affect scan results or availability without prior notice.</li>
              <li>We are not responsible for the accuracy, completeness, or availability of data returned by third-party APIs.</li>
            </ul>

            <h3 className="text-lg font-medium mt-4 mb-3">No Guarantee of Outcomes</h3>
            <ul className="list-disc pl-6 space-y-2 mb-6">
              <li>Using AILens or following its recommendations does not guarantee improved visibility on any AI platform. AI platform behavior is determined by their respective companies and is outside our control.</li>
              <li>Past scan results do not predict future AI platform behavior.</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">Intellectual Property</h2>
            <p className="mb-6">
              All content, features, and functionality of AILens are owned by AILens and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute our methodology, scoring systems, or interface without permission.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Non-Affiliation Disclaimer</h2>
            <p className="mb-6">
              AILens is an independent service and is not affiliated with, endorsed by, or sponsored by OpenAI, Google, Anthropic (Claude), Perplexity AI, or any other AI platform we analyze. All product names, logos, and brands mentioned are property of their respective owners. Our scanning service uses publicly available APIs and does not imply any partnership or official relationship with these platforms.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Refund Policy</h2>
            <p className="mb-6"><strong>No Transactions:</strong> AILens is currently free to use. No paid services are currently offered.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Disclaimer of Warranties</h2>
            <p className="mb-4">
              AILens is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, accuracy, or non-infringement.
            </p>
            <p className="mb-4">
              We do not warrant that: (a) the service will be uninterrupted, timely, secure, or error-free; (b) scan results will be accurate, complete, or current; (c) the technical checks will detect all relevant configurations on your website; (d) any recommendations will achieve specific results; or (e) defects will be corrected.
            </p>
            <p className="mb-6">
              You acknowledge that AI platforms frequently update their models and behavior, and that scan results reflect a point-in-time snapshot that may not remain accurate. Any reliance on scan results or recommendations is at your own risk.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Limitation of Liability</h2>
            <p className="mb-4">
              To the maximum extent permitted by applicable law, AILens and its owner shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business opportunities, or goodwill, arising from: (a) your use of or inability to use the service; (b) any inaccuracy in scan results or technical checks findings; (c) any action taken based on recommendations provided by the service; (d) unauthorized access to your data; or (e) third-party service disruptions affecting our functionality.
            </p>
            <p className="mb-6">
              Our total aggregate liability for all claims arising from or related to the service shall not exceed the amount you have actually paid to AILens in the twelve (12) months preceding the claim, or fifty US dollars ($50), whichever is greater.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Changes</h2>
            <p className="mb-6">We may update these terms. Continued use means acceptance.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Governing Law</h2>
            <p className="mb-6">These terms are governed by applicable law. Any disputes shall be resolved through good-faith negotiation or, if necessary, through appropriate legal channels.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">Contact</h2>
            <p className="mb-6">For any questions or concerns, contact us at <a href="mailto:hello@ai-lens.app" className="text-primary hover:underline">hello@ai-lens.app</a>.</p>

          </div>

          <ProjectDisclaimer />
        </div>
      </div>
    </>
  );
};

export default Terms;
